import { createSingleton } from '@luna/utils';

export type Feature = {
    enabled: boolean;
    _description?: string;
};
// @ts-expect-error we need circular reference
type FeaturesNested = Feature | Record<string, FeaturesNested>;
export type Features = Record<string, FeaturesNested>;

export type FeaturesPathCallback<ApplicationFeatures> = (features: ApplicationFeatures) => Feature;

type FeaturesData<T> = {
    /**
     * Master set of features, merged from all available sources, eg:
     * Default features, flags API overrides, URL & storage overrides
     */
    features: T;
    /**
     * Features without the URL & storage overrides.
     */
    featuresWithoutCustomOverrides: T;
    /**
     * Features as specified in the codebase, without ani overrides
     */
    defaultFeatures: T;
};

/** set() and get() are for application shells usages only */
export const { set, get } = createSingleton<FeaturesData<unknown>>();

export function getGatekeeperFeatures<ApplicationFeatures>(): ApplicationFeatures {
    return get<FeaturesData<ApplicationFeatures>>()?.features;
}
export function getGatekeeperFeaturesWithoutCustomOverrides<
    ApplicationFeatures,
>(): ApplicationFeatures {
    return get<FeaturesData<ApplicationFeatures>>()?.featuresWithoutCustomOverrides;
}
export function getGatekeeperDefaultFeatures<ApplicationFeatures>(): ApplicationFeatures {
    return get<FeaturesData<ApplicationFeatures>>()?.defaultFeatures;
}

/**
 * Checks if a specific feature is enabled in the application.
 *
 * This function uses the Gatekeeper (Flag service) to determine if a feature is enabled.
 * It follows the priority order: URL > LocalStorage > Gatekeeper > country feature.
 *
 * @template ApplicationFeatures The type representing the structure of all application features.
 *
 * @param featurePathCallback A callback function that navigates through the feature structure
 * and returns the specific feature to check.
 *
 * @returns {boolean} True if the feature is enabled, false otherwise.
 * If the feature is not initialized, it returns false.
 *
 * @example
 * import { isFeatureEnabled } from '@luna/gatekeeper';
 *
 * // Basic usage
 * if (isFeatureEnabled((features) => features.infiniteMoney)) {
 *     // ... develop your feature here
 * }
 *
 * // Usage with explicit typing
 * if (isFeatureEnabled<{ infiniteMoney: Feature }>((features) => features.infiniteMoney)) {
 *     // ... develop your feature here
 * }
 *
 * @remarks
 * - This function should always be used to check if a feature is enabled.
 * - If called in static hoisted code, it may resolve sooner than Phoebe flags get merged.
 * - The function uses a singleton pattern to store and retrieve feature data.
 * - In non-production environments, feature flags can be modified at runtime using DevTools (LocalStorage) or URL parameters.
 */
export function isFeatureEnabled<ApplicationFeatures>(
    featurePathCallback: FeaturesPathCallback<ApplicationFeatures>,
): boolean {
    const feature = featurePathCallback(getGatekeeperFeatures<ApplicationFeatures>());
    // flag does not have to be initialized in some run-time environments (tests, different shell)
    return feature?.enabled ?? false;
}

export const setGatekeeperFeatures = set;
